import {
  IOS_BUNDLE_ID,
  IOS_START_URL_HASH,
  PLAY_BILLING_SERVICE_ID,
  SESSION_HAS_ANDROID_TWA_REFERRER_KEY,
  SESSION_HAS_IOS_APP_HASH_KEY,
  SESSION_HAS_IOS_APP_REFERRER_KEY,
  SESSION_HAS_PLAYSTORE_TWA_HASH_KEY,
  SESSION_IS_STANDALONE_MODE_KEY,
  TWA_PACKAGE_NAME,
  TWA_START_URL_HASH,
} from './constants';

declare global {
  interface Navigator {
    getInstalledRelatedApps?: () => Promise<{ platform: string; id: string; url: string; }[]>;
  }
}

// For PWA/TWA install detection,
// see https://thomashunter.name/posts/2021-12-11-detecting-if-pwa-twa-is-installed
// and https://micahjon.com/2021/pwa-twa-detection/

const UA = window?.navigator?.userAgent;
export const IOS = UA?.match(/iPhone|iPad|iPod/);
const ANDROID = UA?.match(/Android/);

export const PLATFORM = IOS ? 'ios' : ANDROID ? 'android' : 'unknown';

// PWA Detection

let hasBeenInStandaloneMode: boolean;
if (isInStandaloneMode()) { // Run this before user has a chance to change display mode
  hasBeenInStandaloneMode = true;
  // Use session storage to avoid sharing PWA session status state with browser
  sessionStorage.setItem(SESSION_IS_STANDALONE_MODE_KEY, 'true');
} else {
  hasBeenInStandaloneMode = sessionStorage.getItem(SESSION_IS_STANDALONE_MODE_KEY) === 'true';
}
const IS_INSTALLED_IOS = IOS && !UA?.match(/Safari/);
const IS_INSTALLED = !!(hasBeenInStandaloneMode || IS_INSTALLED_IOS);

export function isInstalledPwaSession() {
  return !!hasBeenInStandaloneMode;
}

/**
 * Is the page currently in standalone display mode (used by PWA)?
 */
function isInStandaloneMode() {
  return Boolean(
    window?.matchMedia('(display-mode: standalone)').matches
      || (window?.navigator as Navigator & { standalone?: boolean; })?.standalone, // Fallback for iOS
  );
}

// TWA Detection

let isAndroidTwaSession: boolean;
if (document.referrer.includes(`android-app://${TWA_PACKAGE_NAME}`)) {
  isAndroidTwaSession = true;
  sessionStorage.setItem(SESSION_HAS_ANDROID_TWA_REFERRER_KEY, 'true');
} else {
  isAndroidTwaSession = sessionStorage.getItem(SESSION_HAS_ANDROID_TWA_REFERRER_KEY) === 'true';
}

let isIOSAppSession: boolean;
if (document.referrer.includes(`//${IOS_BUNDLE_ID}.ios`)) {
  isIOSAppSession = true;
  sessionStorage.setItem(SESSION_HAS_IOS_APP_REFERRER_KEY, 'true');
} else {
  isIOSAppSession = sessionStorage.getItem(SESSION_HAS_IOS_APP_REFERRER_KEY) === 'true';
}

// Check for #playstore-twa hash in start URL to identify TWA
let hasPlayStoreTwaHash: boolean;
if (window.location.hash === TWA_START_URL_HASH) {
  hasPlayStoreTwaHash = true;
  sessionStorage.setItem(SESSION_HAS_PLAYSTORE_TWA_HASH_KEY, 'true');
} else {
  hasPlayStoreTwaHash = sessionStorage.getItem(SESSION_HAS_PLAYSTORE_TWA_HASH_KEY) === 'true';
}

let hasIOSAppHash: boolean;
if (window.location.hash === IOS_START_URL_HASH) {
  hasIOSAppHash = true;
  sessionStorage.setItem(SESSION_HAS_IOS_APP_HASH_KEY, 'true');
} else {
  hasIOSAppHash = sessionStorage.getItem(SESSION_HAS_IOS_APP_HASH_KEY) === 'true';
}

export function isTwaSession() {
  return hasPlayStoreTwaHash || isAndroidTwaSession || hasIOSAppHash || isIOSAppSession;
}

// Installed store app detection at the end of the file
let isStoreAppInstalled = false;

/**
 * Check if the app is installed from the Play Store.
 * More reliable than isTwaSession() that can return false negative after navigation (OAuth)
 * We don't know if app is currently used here, but we can generally assume it is (iOS/Android app)
 */
export async function isTwaOrPlayStoreInstalled() {
  let isInstalled = isTwaSession() || isStoreAppInstalled;

  if (isInstalled) return true;

  if (window?.navigator?.getInstalledRelatedApps) {
    const relatedApps = await window.navigator.getInstalledRelatedApps();
    isInstalled = isInstalled || relatedApps.some(app => app.platform === 'play' && app.id === TWA_PACKAGE_NAME);
    // Apple does not support detection yet
    isInstalled = isInstalled || relatedApps.some(app => app.platform === 'ios' && app.id === IOS_BUNDLE_ID);
  }
  return isInstalled;
}

export function isFromAppleAppstore() {
  return IS_INSTALLED_IOS || hasAppleStoreKit();
}

export function isAppleDevice() {
  const p = /(Mac|iPhone|iPod|iPad)/i;

  // navigator.platform is deprecated but still in use in Safari
  if (p.test(navigator?.platform || '')) {
    return true;
  }
  return false;
}

/** Checks if the app is installed from the Play Store or App Store,
 * assuming it is currently used if installed if detected with getInstalledRelatedApps.
 * Synchronous for easier use in components
*/
export function isFromAppStores() {
  return isTwaSession() || isStoreAppInstalled || isFromAppleAppstore();
}

/**
 * This is used to hide the monetization features in the app stores with dynamic conditions,
 * such as whether we implemented proper integration with specific app store billing.
*/
export function isHidingMonetizationInStores() {
  return false;
}

/** External payment methods CTAs are currently allowed in the USA storefront */
export function isHidingExternalPaymentCta(storefrontCountry: string | null) {
  return hasAppleStoreKit() && storefrontCountry !== 'USA';
}

export function isStoreDemoAccount(email: string) {
  const addresses = ((import.meta.env.VITE_STORE_DEMO_ACCOUNTS ?? '') as string)
    .split(',')
    .map(a => a.trim());
  return addresses.includes(email);
}

/**
 * Unlike isFromAppStores(), this also checks if an installed PWA is used.
 * Synchronous for easier use in components.
*/
export function isUsingPwaOrStoreAppInstalled() {
  return isTwaSession() || isStoreAppInstalled || IS_INSTALLED;
}

// Google Play Billing Detection

/**
 * Check if we are running in the Google Play Android app with Play Billing supported (Chrome)
*/
export async function hasGooglePlayBilling() {
  const hasDigitalGoodsService = window?.getDigitalGoodsService !== undefined;

  if (!hasDigitalGoodsService) return false;
  
  try {
    const isGooglePlayBilling = await window.getDigitalGoodsService(PLAY_BILLING_SERVICE_ID);
    return !!isGooglePlayBilling;
  } catch (_) {
    return false;
  }
}

// Installed store app detection

(async () => {
  try {
    if (window?.navigator?.getInstalledRelatedApps) {
      const relatedApps = await window.navigator.getInstalledRelatedApps();
      isStoreAppInstalled = isStoreAppInstalled || relatedApps.some(app => app.platform === 'play' && app.id === TWA_PACKAGE_NAME);
      // Apple does not support detection yet
      isStoreAppInstalled = isStoreAppInstalled || relatedApps.some(app => app.platform === 'ios' && app.id === IOS_BUNDLE_ID);
    }
  } catch (e) {
    console.error('getInstalledRelatedApps error', e);
  }
})();

// Apple StoreKit Detection

export function hasAppleStoreKit() {
  return !!(window.webkit?.messageHandlers?.['iap-purchase-request']);
}
